import React from "react";
import { ArrowRight, Bookmark, Check, Clock, Globe, MapPin, Phone, Share2, X } from "lucide-react";
import { useCart } from "../../contexts/CartContext";
import { parseLatLng, normalizeWebsite, haversine, isValidPhone } from "../../utils/geo";
import { searchService } from "../../services/searchService";

const MAX_TITLE_LENGTH = 72;
const MAX_HOURS_LENGTH = 42;

function truncate(text, max) {
  if (!text) return "";
  const clean = String(text).replace(/<[^>]+?>/gm, "");
  if (clean.length <= max) return clean;
  return clean.slice(0, max - 1) + "...";
}

function MapResultCard({ result, searchCoords, onClose, onSelect }) {
  const { contains, addItem, removeItem } = useCart();

  if (!result) return null;

  const inCart = contains(result);
  const latLng = result.latLng || parseLatLng(result.location);
  const addressLine = result.addressLine || [result.address_1, result.address_2, result.city, result.state]
    .filter(Boolean)
    .join(", ");
  const organizationName = [result.agency_name, result.site_name].filter(Boolean).join(" - ");
  const rawPhone = result.program_site_phones?.phone_1
    || result.program_site_phones?.phone_2
    || result.program_phones?.phone_1
    || result.site_phones?.phone_1
    || result.agency_phones?.phone_1
    || result.phone;
  const phone = isValidPhone(rawPhone) ? rawPhone : null;
  const website = normalizeWebsite(result.program_site_website || result.program_website || result.site_website || result.agency_website || result.website || "");
  const hours = truncate(result.hours_of_operation || result.hours, MAX_HOURS_LENGTH);
  const distance = searchCoords && latLng ? haversine(searchCoords, latLng, { unit: "mile" }) : null;

  function handleCartToggle() {
    if (inCart) {
      removeItem(result);
    } else {
      addItem(result);
    }
  }

  function handleShare() {
    if (navigator.share) {
      navigator.share({
        title: result.name || result.agency_name,
        url: `${window.location.origin}/search/${result.program_site_id}`,
      }).catch(() => {});
      return;
    }

    navigator.clipboard?.writeText(`${window.location.origin}/search/${result.program_site_id}`);
  }

  function stopMapEvent(e) {
    e.stopPropagation();
  }

  function handleClose(e) {
    stopMapEvent(e);
    onClose?.();
  }

  return (
    <article
      className="map-result-card"
      aria-label={result.name || result.agency_name}
      onClick={stopMapEvent}
      onMouseDown={stopMapEvent}
    >
      <button className="map-result-card-close" type="button" aria-label="Close result" onClick={handleClose}>
        <X aria-hidden="true" size={18} strokeWidth={1.8} />
      </button>

      <div className="map-result-card-body">
        <h2 className="map-result-card-title">{truncate(result.name || result.agency_name, MAX_TITLE_LENGTH)}</h2>
        {organizationName && <p className="map-result-card-agency">at {organizationName}</p>}

        <div className="map-result-card-meta">
          {distance != null && <span className="map-result-card-distance">{distance.toFixed(1)} miles away</span>}

          {addressLine && latLng && (
            <a
              className="map-result-card-line map-result-card-address"
              href={`https://www.google.com/maps/dir/?api=1&destination=${latLng.lat},${latLng.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => searchService.trackActivity({ action: "directions", name: result.name, site_name: result.site_name, program_at_site_number: result.program_site_id })}
            >
              <MapPin aria-hidden="true" size={16} strokeWidth={1.8} />
              <span>{addressLine}</span>
            </a>
          )}

          {addressLine && !latLng && (
            <div className="map-result-card-line map-result-card-address">
              <MapPin aria-hidden="true" size={16} strokeWidth={1.8} />
              <span>{addressLine}</span>
            </div>
          )}

          {phone && (
            <a
              className="map-result-card-line"
              href={`tel:${phone.replace(/\D/g, "")}`}
              onClick={() => searchService.trackActivity({ action: "phone", name: result.name, site_name: result.site_name, program_at_site_number: result.program_site_id })}
            >
              <Phone aria-hidden="true" size={16} strokeWidth={1.8} />
              <span>{phone}</span>
            </a>
          )}

          {hours && (
            <div className="map-result-card-line">
              <Clock aria-hidden="true" size={16} strokeWidth={1.8} />
              <span>{hours}</span>
            </div>
          )}
        </div>
      </div>

      <div className="map-result-card-footer">
        <div className="map-result-card-actions">
          {website && (
            <a className="map-result-card-icon" href={website} target="_blank" rel="noopener noreferrer" aria-label="Website" title="Website">
              <Globe aria-hidden="true" size={17} strokeWidth={1.8} />
            </a>
          )}
          <button className="map-result-card-icon" type="button" onClick={handleCartToggle} aria-label={inCart ? "Remove from List" : "Add to List"} title={inCart ? "Remove from List" : "Add to List"}>
            {inCart ? <Check aria-hidden="true" size={17} strokeWidth={1.8} /> : <Bookmark aria-hidden="true" size={17} strokeWidth={1.8} />}
          </button>
          <button className="map-result-card-icon" type="button" onClick={handleShare} aria-label="Share" title="Share">
            <Share2 aria-hidden="true" size={17} strokeWidth={1.8} />
          </button>
        </div>

        <button className="map-result-card-details" type="button" onClick={() => onSelect && onSelect(result)}>
          <span>Details</span>
          <ArrowRight aria-hidden="true" size={17} strokeWidth={1.8} />
        </button>
      </div>
    </article>
  );
}

export default MapResultCard;
